import React from "react"
import { ScrollReveal } from "../ui/scroll-reveal"

export function ApproachSection() {
  return (
    <section className="py-16">
      <div className="container space-y-12">
        {/* About Syllepsis */}
        <ScrollReveal>
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <p className="text-lg text-muted-foreground">
              Founded in 2024, Syllepsis is an AI innovation and knowledge engineering firm. We help organisations structure their institutional knowledge - the processes, expertise and operational wisdom that makes experienced teams effective - into AI-ready systems that create sustainable advantage.
            </p>
          </div>
        </ScrollReveal>

        {/* Why AI Initiatives Struggle */}
        <ScrollReveal delay={0.1}>
          <div className="max-w-3xl mx-auto space-y-4">
            <h2 className="text-2xl font-bold tracking-tighter sm:text-3xl text-center">
              Why AI Initiatives Struggle
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                Everyone has access to the same AI models. Yet most organisations struggle to extract meaningful business value from their AI investments.
              </p>
              <p>
                The reason is straightforward. Generic AI tools lack the context to operate effectively in your specific environment. They don't understand your terminology, your processes, your quality standards or the unwritten rules that make experienced professionals effective.
              </p>
              <p>
                This isn't a technology problem - it's a knowledge problem. And solving knowledge problems requires expertise, not just better tools.
              </p>
            </div>
          </div>
        </ScrollReveal>

        {/* How We Work */}
        <ScrollReveal delay={0.2}>
          <div className="max-w-4xl mx-auto space-y-8">
            <h2 className="text-2xl font-bold tracking-tighter sm:text-3xl text-center">
              How We Work
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  title: "Consulting-Led Discovery",
                  description: "Our consultants work directly with your teams through structured interviews, working sessions and hands-on collaboration. We capture the knowledge that lives in people's heads - the decision frameworks, operational wisdom and domain expertise that makes experienced professionals effective. This isn't document processing. It's systematic knowledge engineering led by people who understand both the methodology and your operational context.",
                  step: "01"
                },
                {
                  title: "Knowledge Architecture",
                  description: "We structure institutional knowledge into systems that AI can consume and reason over. The Knowledge Architecture we create belongs to you - it's your institutional knowledge, structured and accessible. And unlike AI tools that depreciate and require rebuilding, structured knowledge becomes more valuable over time as AI models advance.",
                  step: "02"
                },
                {
                  title: "Flexible Engagement",
                  description: "Different organisations need different things. Some want to build internal AI capability and operate independently. Others want an ongoing partnership where we provide and operate AI systems on their behalf. We adapt to what fits your organisation's needs, resources and strategic objectives.",
                  step: "03"
                }
              ].map((phase) => (
                <div
                  key={phase.title}
                  className="relative bg-primary/5 rounded-lg p-8 border border-primary/10"
                >
                  <div className="absolute -top-4 -left-4 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">
                    {phase.step}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{phase.title}</h3>
                  <p className="text-muted-foreground text-sm">{phase.description}</p>
                </div>
              ))}
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  )
} 